/**
 * PercorsoLead — a che punto è un'azienda e cosa abbiamo fatto con lei (CRM di piattaforma, 20/10/2026).
 *
 * In cima il prossimo passo (una frase: è la cosa da leggere per prima; lo stato è nella testata della scheda);
 * sotto la linea del tempo del
 * registro delle azioni, dalla voce più recente, con un pallino colorato da CHI ha fatto la cosa (in automatico,
 * dalla skill, dalla console). La console legge e basta: le voci le scrive il server, e lo stato e il prossimo
 * passo li ricava lui (`aggiornaLeadDalRegistro`). Se una scheda vecchia non li ha ancora, il prossimo passo si
 * calcola con la stessa regola condivisa (`prossimoPasso`), mai con una copia.
 */

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { prossimoPasso, type AutoreVoceRegistro } from '@shared/questionari';
import { tokens } from '../../theme';
import { listRegistroLead } from '../../services/platformCrmService';
import type { PlatformLead, VoceRegistroLead } from '../../types/platformCrm';
import { formatDateTime } from './labels';

const COLORE_AUTORE: Record<AutoreVoceRegistro, string> = {
  sistema: tokens.color.mist,
  skill: tokens.color.tierBusiness.main,
  console: tokens.color.tierPro.main,
};

export const PercorsoLead: React.FC<{ lead: PlatformLead }> = ({ lead }) => {
  const { t } = useTranslation('crm');
  const [voci, setVoci] = useState<VoceRegistroLead[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    setVoci(null);
    setErrore(null);
    listRegistroLead(lead.id)
      .then((v) => { if (!annullato) setVoci(v); })
      .catch((e: unknown) => {
        console.error('[platform-crm] lettura del registro fallita', { lead_id: lead.id, errore: e });
        if (!annullato) setErrore(e instanceof Error ? e.message : String(e));
      });
    return () => { annullato = true; };
    // Si rilegge quando cambia l'azienda o quando il server ha scritto una voce nuova.
  }, [lead.id, lead.registro_aggiornato_at?.toMillis?.()]);

  const passo = lead.prossimo_passo ?? prossimoPasso(lead.stato, lead.registro_ultima?.tipo ?? null);

  return (
    <Box component="section" aria-labelledby="percorso-titolo">
      <Typography id="percorso-titolo" variant="subtitle1" fontWeight={700} sx={{ mb: 1.25 }}>
        {t('percorso.titolo')}
      </Typography>

      {/* Lo stato è già nella testata della scheda: qui solo la frase del prossimo passo. */}
      <Typography
        sx={{
          mb: 2,
          fontFamily: tokens.font.display,
          fontWeight: 500,
          fontSize: '1.05rem',
          lineHeight: 1.3,
          letterSpacing: '-0.01em',
          color: passo === 'nessuno' ? tokens.color.ash : tokens.color.graphite,
        }}
      >
        {t('percorso.prossimo_passo', { passo: t(`percorso.passo.${passo}`) })}
      </Typography>

      {errore ? (
        <Typography variant="body2" sx={{ color: tokens.color.danger }}>{t('percorso.errore', { errore })}</Typography>
      ) : voci === null ? (
        <Box sx={{ py: 2 }}><CircularProgress size={18} /></Box>
      ) : voci.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.color.mist }}>{t('percorso.vuoto')}</Typography>
      ) : (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {voci.map((v, i) => (
            <Box
              component="li"
              key={v.id}
              sx={{ display: 'grid', gridTemplateColumns: '96px 18px 1fr', columnGap: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ color: tokens.color.ash, pt: '2px', lineHeight: 1.35, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatDateTime(v.quando)}
              </Typography>
              {/* Il filo: il pallino di chi ha fatto la cosa, e la linea fino alla voce dopo. */}
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 9, height: 9, mt: '6px', borderRadius: '50%', flexShrink: 0, zIndex: 1,
                    backgroundColor: COLORE_AUTORE[v.da] ?? tokens.color.mist,
                    boxShadow: `0 0 0 3px ${tokens.color.paper}`,
                  }}
                />
                {i < voci.length - 1 && (
                  <Box aria-hidden sx={{ position: 'absolute', top: 15, bottom: 0, width: '1px', backgroundColor: tokens.color.line }} />
                )}
              </Box>
              <Box sx={{ pb: i < voci.length - 1 ? 2 : 0, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.35 }}>
                  {t(`percorso.voce.${v.tipo}`)}
                  <Typography component="span" variant="caption" sx={{ color: tokens.color.mist, fontWeight: 500, ml: 0.75 }}>
                    {t(`percorso.autore.${v.da}`)}
                  </Typography>
                </Typography>
                {v.riassunto && (
                  <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.25, overflowWrap: 'anywhere' }}>
                    {v.riassunto}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
